/**
 * Notices when the server has gone away — a deploy, a restart, a crash — and
 * tells the user, instead of letting every click fail one at a time.
 *
 * Desk, 2026-09-27: users lose actions while the backend is being updated and
 * conclude the application is bad. What actually happens is a few seconds to a
 * few minutes in which requests are refused or answered by nginx (502/503/504).
 *
 * ONE FAILURE IS NOT AN OUTAGE. A single dropped request on a flaky network must
 * not freeze a clerk's screen, so a failure only STARTS a check: the server's own
 * `/health` is asked up to three times, two seconds apart. Any healthy answer
 * means it was a blip and normal error handling carries on. Three misses in a row
 * is an outage; the notice goes up and a slower poll waits for it to come back.
 *
 * `/health` is proxied at the site root on both production hostnames, and is
 * fetched with plain `fetch` on purpose — going through the axios client would
 * feed its own failures back into this module.
 */
import { isBackendOutage } from '@core/errors/api-error-handler';
import { useSystemStatusStore } from '@store/system-status.store';

export const PROBE_TIMEOUT_MS = 4000;
export const CONFIRM_ATTEMPTS = 3;
export const CONFIRM_GAP_MS = 2000;
export const RECOVERY_POLL_MS = 5000;

export type OutageVerdict = 'down' | 'blip';

/** One check of the server's own health endpoint. Never throws. */
export async function probeHealth(): Promise<boolean> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    const res = await fetch('/health', { cache: 'no-store', signal: controller.signal });
    if (!res.ok) return false;
    const body = (await res.json().catch(() => null)) as { status?: string } | null;
    return body?.status === 'ok';
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

let inFlight: Promise<OutageVerdict> | null = null;
let poller: ReturnType<typeof setInterval> | null = null;

function startRecoveryPolling(): void {
  if (poller) return;
  poller = setInterval(() => void checkNow(), RECOVERY_POLL_MS);
}

function stopRecoveryPolling(): void {
  if (poller) clearInterval(poller);
  poller = null;
}

/** Asks the server right now (the notice's "Check now" and its own poll). Moves the notice on to "recovered" when it answers. */
export async function checkNow(): Promise<boolean> {
  const ok = await probeHealth();
  const store = useSystemStatusStore.getState();
  if (store.phase !== 'updating') return ok;
  store.markChecked();
  if (ok) {
    stopRecoveryPolling();
    store.markRecovered();
  }
  return ok;
}

async function confirmOutage(): Promise<OutageVerdict> {
  for (let attempt = 0; attempt < CONFIRM_ATTEMPTS; attempt += 1) {
    if (await probeHealth()) return 'blip';
    if (attempt < CONFIRM_ATTEMPTS - 1) await sleep(CONFIRM_GAP_MS);
  }
  useSystemStatusStore.getState().markUpdating();
  startRecoveryPolling();
  return 'down';
}

/** Called by the HTTP client for every failed request; ignores anything that is not an outage shape. */
export function reportBackendFailure(error: unknown): void {
  if (!isBackendOutage(error)) return;
  if (useSystemStatusStore.getState().phase === 'updating') return;
  if (!inFlight) {
    inFlight = confirmOutage().finally(() => {
      inFlight = null;
    });
  }
}

/**
 * The verdict for a failure that was just reported, or null when no check is
 * running and the notice is not up (so nothing is waiting on an answer).
 * `notify` holds outage-caused errors until this settles: 'down' means the
 * update notice already says everything, 'blip' means show the error as usual.
 */
export function outageVerdict(): Promise<OutageVerdict> | null {
  if (useSystemStatusStore.getState().phase === 'updating') return Promise.resolve('down');
  return inFlight;
}

/** A page chunk that no longer exists means a new version was installed under this open screen. */
export function watchForNewVersion(target: Pick<Window, 'addEventListener'> = window): void {
  target.addEventListener('vite:preloadError', () => useSystemStatusStore.getState().markReload());
}

/** Test seam. */
export function resetBackendAvailability(): void {
  stopRecoveryPolling();
  inFlight = null;
}
